import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { Modal, ModalSize, Pagination } from '@/components';
import { Table } from '@/ui';
import { useAppDispatch, useAppState } from '@/store';
import { formatPercentValue } from '@/utils';
import { ICancelledReasonItem } from '@/models/cancelledOrders.model';
import { cancelledOrdersSelector, getCancelledReasonsList } from '../../feature';
import DynamicsCell from '../DynamicsCell/DynamicsCell';

interface IAllReasonsModalProps {
  open: boolean;
  onClose: () => void;
}

const AllReasonsModal = ({ open, onClose }: IAllReasonsModalProps) => {
  const { t, i18n } = useTranslation(['dashboard', 'orders']);
  const dispatch = useAppDispatch();
  const { reasonsList, reasonsMeta, isListLoading, query } = useAppState(cancelledOrdersSelector);
  const [page, setPage] = useState(1);
  const ppSuffix = t('cancelled_orders_pp_suffix');

  useEffect(() => {
    if (!open) return;
    dispatch(
      getCancelledReasonsList({
        date_from: query.date_from,
        date_to: query.date_to,
        page,
        per_page: 20,
      }),
    );
  }, [open, page, query.date_from, query.date_to, dispatch]);

  const columns: ColumnsType<ICancelledReasonItem> = [
    {
      title: t('cancelled_orders_reason'),
      dataIndex: 'code',
      key: 'code',
      render: (code: string, record) =>
        record.label || t(`cancellation_reason_${code}`, { ns: 'orders', defaultValue: code }),
    },
    {
      title: t('cancelled_orders_share'),
      dataIndex: 'share_percent',
      key: 'share_percent',
      render: (value: number) => formatPercentValue(value, 1, i18n.language),
    },
    {
      title: t('cancelled_orders_dynamics'),
      dataIndex: 'change_percent',
      key: 'change_percent',
      render: (value: number | null) => <DynamicsCell value={value} suffix={ppSuffix} />,
    },
    {
      title: t('cancelled_orders_count'),
      dataIndex: 'count',
      key: 'count',
      render: (value?: number) => value ?? '—',
    },
  ];

  return (
    <Modal visible={open} onCancel={onClose} size={ModalSize.HUGE} title={t('cancelled_orders_all_reasons_title')}>
      <Table
        rowKey={(row) => `${row.code}-${row.label ?? ''}`}
        columns={columns}
        dataSource={reasonsList}
        loading={isListLoading}
      />
      {reasonsMeta ? (
        <Pagination
          current={reasonsMeta.current_page}
          total={reasonsMeta.total}
          pageSize={reasonsMeta.per_page}
          onChange={setPage}
        />
      ) : null}
    </Modal>
  );
};

export default AllReasonsModal;
